/**
 * What every screen needs: the firm (GET /firm), the app's runtime config, and who's
 * signed in (GET /driver/me). Loaded once when the app opens.
 */
import { ApiError, get, onSignedOut, post } from "@taxi/mobile-web/api";
import { applyColours, loadAppConfig } from "@taxi/mobile-web/config";
import { closeRealtime } from "@taxi/mobile-web/realtime";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import brand from "../../../../brand/brand.json";
import type { DriverMe, FirmInfo } from "./types";

type Session = {
  firm: FirmInfo;
  /** Null when signed out. */
  me: DriverMe | null;
  setMe: (me: DriverMe | null) => void;
  refreshMe: () => Promise<DriverMe | null>;
  signOut: () => Promise<void>;
  /** The office's number, for drivers to call. */
  firmPhone: string;
};

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error("useSession outside SessionProvider");
  return session;
}

/** For screens that are only reachable when signed in. */
export function useMe(): DriverMe {
  const { me } = useSession();
  if (!me) throw new Error("Not signed in");
  return me;
}

/** The driver, or null when the session has ended. */
export async function fetchMe(): Promise<DriverMe | null> {
  try {
    return await get<DriverMe>("/driver/me");
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

type Loaded = { firm: FirmInfo; me: DriverMe | null };

async function load(): Promise<Loaded> {
  const [firm, , me] = await Promise.all([get<FirmInfo>("/firm"), loadAppConfig(), fetchMe()]);
  return { firm, me };
}

export function SessionProvider({
  children,
  loading,
  failed,
}: {
  children: ReactNode;
  loading: ReactNode;
  failed: (retry: () => void) => ReactNode;
}) {
  const [state, setState] = useState<Loaded | null>(null);
  const [error, setError] = useState(false);

  const start = useCallback(() => {
    setError(false);
    load()
      .then((loaded) => {
        applyColours(loaded.firm.colours);
        setState(loaded);
      })
      .catch(() => setError(true));
  }, []);

  useEffect(start, [start]);

  // Signed out somewhere else, or the session ended: back to the start.
  useEffect(
    () =>
      onSignedOut(() => {
        closeRealtime();
        setState((current) => (current ? { ...current, me: null } : current));
      }),
    [],
  );

  const setMe = useCallback(
    (me: DriverMe | null) => setState((current) => (current ? { ...current, me } : current)),
    [],
  );

  const refreshMe = useCallback(async () => {
    const me = await fetchMe();
    setMe(me);
    return me;
  }, [setMe]);

  const signOut = useCallback(async () => {
    closeRealtime();
    await post("/auth/logout").catch(() => undefined);
    setMe(null);
  }, [setMe]);

  if (error) return <>{failed(start)}</>;
  if (!state) return <>{loading}</>;
  return (
    <SessionContext.Provider
      value={{
        ...state,
        setMe,
        refreshMe,
        signOut,
        firmPhone: state.firm.phone || brand.contact.phone,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}
